import { useEffect } from "react";
import { SuperSEO } from "react-super-seo";
import { Helmet } from 'react-helmet';

function Terms() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            id: "eligibility",
            num: "01",
            title: "Eligibility Requirements",
            icon: "fa-solid fa-user-check",
            badge: "Mandatory",
            content: (
                <div className="text-muted" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                    <p className="mb-3">
                        To use our services, you must be at least <strong>25 years old</strong> and possess a valid driving license (for self-drive services). Users must provide accurate and complete identification information during booking.
                    </p>
                    <div className="p-3 rounded bg-light border-left border-primary" style={{ borderLeftWidth: '4px !important' }}>
                        <span className="font-weight-bold text-dark d-block mb-1">Required Identification Documents:</span>
                        <span className="small text-secondary">Valid Driving License (held for min 2 years), Aadhaar Card or Passport, and verified phone number.</span>
                    </div>
                </div>
            )
        },
        {
            id: "booking-payment",
            num: "02",
            title: "Booking & Payment",
            icon: "fa-solid fa-wallet",
            badge: "Reservations",
            content: (
                <ul className="list-unstyled mb-0" style={{ fontSize: '15px', lineHeight: '1.8', color: '#475569' }}>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>All bookings must be made through our official website or authorized platforms.</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Payment can be made via UPI or Direct Bank Transfer (IMPS/NEFT/RTGS).</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>A refundable security deposit may be required for self-drive rentals prior to vehicle dispatch.</span>
                    </li>
                    <li className="mb-0 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Cancellations and refunds are processed strictly per our cancellation policy outlined below.</span>
                    </li>
                </ul>
            )
        },
        {
            id: "vehicle-usage",
            num: "03",
            title: "Vehicle Usage & Responsibilities",
            icon: "fa-solid fa-car-side",
            badge: "Rules of Road",
            content: (
                <div>
                    <ul className="list-unstyled mb-3" style={{ fontSize: '15px', lineHeight: '1.8', color: '#475569' }}>
                        <li className="mb-2 d-flex align-items-start">
                            <span className="text-primary font-weight-bold mr-2">✓</span>
                            <span>The rented vehicle must be used exclusively for lawful travel purposes within permitted regions.</span>
                        </li>
                        <li className="mb-2 d-flex align-items-start">
                            <span className="text-primary font-weight-bold mr-2">✓</span>
                            <span>Users are fully responsible for any traffic violations, speeding fines, toll charges, or parking penalties incurred during the rental period.</span>
                        </li>
                        <li className="mb-2 d-flex align-items-start">
                            <span className="text-primary font-weight-bold mr-2">✓</span>
                            <span><strong>Fuel Policy:</strong> Vehicles must be returned with the same fuel level as received at trip pickup.</span>
                        </li>
                    </ul>
                    <div className="p-3 rounded" style={{ backgroundColor: '#fff1f2', border: '1px solid #fecdd3' }}>
                        <span className="text-danger font-weight-bold small d-block mb-1">
                            <i className="fa-solid fa-ban mr-1"></i> Strictly Prohibited:
                        </span>
                        <span className="small text-danger" style={{ lineHeight: '1.5' }}>
                            Smoking, drinking alcohol, substance consumption, sub-letting, racing, or engaging in illegal activities inside or with the vehicle is strictly prohibited.
                        </span>
                    </div>
                </div>
            )
        },
        {
            id: "insurance-liability",
            num: "04",
            title: "Insurance & Damage Liability",
            icon: "fa-solid fa-shield",
            badge: "Important",
            content: (
                <div>
                    <p className="text-muted mb-3" style={{ fontSize: '15px' }}>
                        Any physical or operational damage caused to the vehicle during the rental period is the sole responsibility of the user.
                    </p>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="p-3 rounded h-100" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <div className="d-flex align-items-center mb-2">
                                    <span className="badge badge-warning text-dark mr-2 px-2 py-1">Up to ₹15,000</span>
                                    <strong className="text-dark small">Minor Damages</strong>
                                </div>
                                <p className="small text-muted mb-0">
                                    If the repair cost is <strong>₹15,000 or less</strong>, the user must pay the full repair amount out of pocket.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="p-3 rounded h-100" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <div className="d-flex align-items-center mb-2">
                                    <span className="badge badge-primary mr-2 px-2 py-1">Exceeding ₹15,000</span>
                                    <strong className="text-dark small">Major Damages</strong>
                                </div>
                                <p className="small text-muted mb-0">
                                    If repair exceeds <strong>₹15,000</strong>, insurance claims will be processed. The user remains responsible for <strong>depreciation & non-claimable charges</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="p-3 rounded mb-3" style={{ backgroundColor: '#fefce8', border: '1px solid #fef08a' }}>
                        <ul className="list-unstyled mb-0 small text-warning-800" style={{ color: '#854d0e', lineHeight: '1.6' }}>
                            <li className="mb-1">
                                ⚠️ <strong>Insurance Claim Denial:</strong> If insurance denies a claim due to user negligence or policy breach, the user pays the full repair amount.
                            </li>
                            <li className="mb-1">
                                ⏱️ <strong>Downtime Rent:</strong> Users are liable to pay daily rental charges for the number of repair days the vehicle remains out of service.
                            </li>
                            <li>
                                💼 <strong>Personal Belongings:</strong> Aapli Gaadi is not liable for personal items left, lost, or stolen inside the vehicle.
                            </li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: "pick-drop",
            num: "05",
            title: "Pick & Drop Transfer Services",
            icon: "fa-solid fa-location-dot",
            badge: "Transfers",
            content: (
                <ul className="list-unstyled mb-0" style={{ fontSize: '15px', lineHeight: '1.8', color: '#475569' }}>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Users must provide accurate pick-up and drop-off locations, dates, and flight/train timings.</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Delays or changes to itineraries must be communicated via customer support in advance.</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Users must be present at the designated location at the agreed pickup time.</span>
                    </li>
                    <li className="mb-0 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Additional charges may apply for extended waiting times or unauthorized route detours.</span>
                    </li>
                </ul>
            )
        },
        {
            id: "driver-services",
            num: "06",
            title: "Chauffeur & Driver Services",
            icon: "fa-solid fa-user-tie",
            badge: "Chauffeurs",
            content: (
                <ul className="list-unstyled mb-0" style={{ fontSize: '15px', lineHeight: '1.8', color: '#475569' }}>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Our chauffeurs are verified, background-checked, and experienced professionals.</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Users must treat drivers with respect and adhere to standard vehicle safety guidelines.</span>
                    </li>
                    <li className="mb-0 d-flex align-items-start">
                        <span className="text-primary font-weight-bold mr-2">✓</span>
                        <span>Any driver misconduct or itinerary disputes should be reported immediately to our support team.</span>
                    </li>
                </ul>
            )
        },
        {
            id: "cancellation",
            num: "07",
            title: "Cancellation & Refund Policy",
            icon: "fa-solid fa-arrow-rotate-left",
            badge: "Refunds",
            content: (
                <div>
                    <p className="text-muted mb-3" style={{ fontSize: '15px' }}>
                        Cancellation charges and refund percentages apply as per the schedule below:
                    </p>
                    <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm text-center mb-0" style={{ fontSize: '14px' }}>
                            <thead className="thead-dark">
                                <tr>
                                    <th>Cancellation Window</th>
                                    <th>Refund Eligibility (% of Total Rental)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="align-middle">More than 72 hours before trip start</td>
                                    <td className="align-middle text-success font-weight-bold">90% Refund of Total Rental</td>
                                </tr>
                                <tr>
                                    <td className="align-middle">Between 24 to 72 hours before trip start</td>
                                    <td className="align-middle text-warning-dark font-weight-bold" style={{ color: '#d97706' }}>75% Refund of Total Rental</td>
                                </tr>
                                <tr>
                                    <td className="align-middle">Less than 24 hours before trip start</td>
                                    <td className="align-middle text-danger font-weight-bold">No Refund (0%)</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="p-3 rounded mb-2" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <span className="font-weight-bold text-primary small d-block mb-1">
                            <i className="fa-solid fa-circle-info mr-1"></i> Calculation Basis Note:
                        </span>
                        <span className="small" style={{ color: '#1e40af', lineHeight: '1.6' }}>
                            Please note that all cancellation & refund percentages (90%, 75%, 0%) are calculated on the <strong>Total Rental Amount</strong> of the booking, and <strong>NOT</strong> solely on the advance token amount paid during reservation.
                        </span>
                    </div>
                    <p className="small text-muted mb-0">
                        * Eligible refunds are processed automatically back to the original payment source within <strong>7 to 10 business days</strong>.
                    </p>
                </div>
            )
        },
        {
            id: "privacy-policy",
            num: "08",
            title: "Privacy & Data Protection",
            icon: "fa-solid fa-lock",
            badge: "Privacy",
            content: (
                <p className="text-muted mb-0" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                    We value your privacy and handle all personal identification data in compliance with our Privacy Policy. By accessing or using our services, you consent to the secure collection, processing, and storage of your information for reservation and legal verification purposes.
                </p>
            )
        },
        {
            id: "terms-changes",
            num: "09",
            title: "Policy Updates & Modifications",
            icon: "fa-solid fa-pen-to-square",
            badge: "Updates",
            content: (
                <p className="text-muted mb-0" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                    Aapli Gaadi reserves the right to update or modify these terms at any time without prior notice. Continued use of our website or rental services following any changes constitutes your explicit acceptance of the revised Terms and Conditions.
                </p>
            )
        }
    ];

    return (
        <>
            <Helmet>
                <link rel="canonical" href="https://www.aapligaadi.co.in/terms-and-conditions" />
            </Helmet>
            <SuperSEO
                title="Terms and Conditions - Aapli Gaadi Car Rental Services"
                description="Read the terms and conditions for Aapli Gaadi’s car rental services across Mumbai, Navi Mumbai, Thane, Kudal, Kankavli, Vaibhavwadi, Sawantwadi, Vengurla, Malvan, Tarkarli, Kunkeshwar, Devgad, Devbag, Ratnagiri, and Ganpatipule. Learn about booking policies, rental agreements, cancellations, and user responsibilities."
                lang="en"
                openGraph={{
                    ogTitle: "Terms and Conditions - Aapli Gaadi Car Rentals",
                    ogDescription:
                        "Review the terms and conditions for Aapli Gaadi’s self-drive and chauffeur-driven car rental services across Maharashtra.",
                    ogUrl: "https://www.aapligaadi.co.in/terms-and-conditions",
                    ogType: "website",
                    ogSiteName: "Aapli Gaadi",
                    ogLocale: "en_US"
                }}
            />

            {/* Header Hero Banner */}
            <section className="hero-wrap hero-wrap-2 js-fullheight" style={{ backgroundImage: `url('image/aapli_gaadi_terms.png')` }} data-stellar-background-ratio="0.5">
                <div className="overlay"></div>
                <div className="container">
                    <div className="row no-gutters slider-text js-fullheight align-items-end justify-content-start">
                        <div className="col-md-9 pb-5">
                            <p className="breadcrumbs">
                                <span className="mr-2"><a href="/">Home <i className="ion-ios-arrow-forward"></i></a></span>
                                <span>Terms & Conditions <i className="ion-ios-arrow-forward"></i></span>
                            </p>
                            <h1 className="mb-3 bread playwrite-cu-font">Terms & Conditions</h1>
                            <p className="text-white-50 lead mb-0" style={{ fontSize: '16px' }}>
                                Clear, transparent guidelines for a hassle-free rental experience.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content Area */}
            <section className="ftco-section bg-light py-5">
                <div className="container">
                    
                    {/* Introduction Banner Card */}
                    <div className="card border-0 shadow-sm mb-4 rounded-lg overflow-hidden" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4 p-md-5" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff' }}>
                            <div className="d-flex align-items-center mb-3">
                                <div className="d-flex align-items-center justify-content-center rounded-circle mr-3" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(1, 210, 142, 0.2)', color: '#01d28e' }}>
                                    <i className="fa-solid fa-file-contract" style={{ fontSize: '22px' }}></i>
                                </div>
                                <div>
                                    <h2 className="h4 font-weight-bold text-white mb-0">Welcome to Aapli Gaadi</h2>
                                    <span className="small text-white-50">Legal Agreement & Service Rules</span>
                                </div>
                            </div>
                            <p className="mb-0" style={{ fontSize: '15px', lineHeight: '1.7', color: '#cbd5e1' }}>
                                These Terms and Conditions outline the rules and regulations for utilizing Aapli Gaadi’s rental services, including self-drive cars, chauffeur rentals, self-drive bikes, and station transfer services across Maharashtra. By booking or utilizing our services, you agree to comply with these terms.
                            </p>
                        </div>
                    </div>

                    {/* Section Cards */}
                    <div className="row">
                        {sections.map((sec) => (
                            <div className="col-12 mb-4" key={sec.id} id={sec.id}>
                                <div className="card border-0 shadow-sm rounded-lg" style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <div className="card-body p-4 p-md-4">
                                        <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-3">
                                            <div className="d-flex align-items-center">
                                                <span className="font-weight-bold text-primary mr-3" style={{ fontSize: '20px', opacity: 0.8 }}>{sec.num}</span>
                                                <div className="d-flex align-items-center justify-content-center rounded mr-3" style={{ width: '38px', height: '38px', backgroundColor: 'rgba(1, 210, 142, 0.1)', color: '#01d28e' }}>
                                                    <i className={sec.icon} style={{ fontSize: '16px' }}></i>
                                                </div>
                                                <h3 className="h5 font-weight-bold text-dark mb-0">{sec.title}</h3>
                                            </div>
                                            <span className="badge badge-light px-3 py-2 text-muted font-weight-normal border d-none d-sm-inline-block" style={{ borderRadius: '20px', fontSize: '12px' }}>
                                                {sec.badge}
                                            </span>
                                        </div>
                                        {sec.content}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Support & Assistance Banner */}
                    <div className="card border-0 shadow-sm rounded-lg overflow-hidden mt-4" style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div className="card-body p-4 p-md-5 text-center bg-white">
                            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3" style={{ width: '60px', height: '60px', backgroundColor: 'rgba(1, 210, 142, 0.1)', color: '#01d28e' }}>
                                <i className="fa-solid fa-headset" style={{ fontSize: '28px' }}></i>
                            </div>
                            <h3 className="h4 font-weight-bold text-dark mb-2">Have Questions About Our Terms?</h3>
                            <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '600px', fontSize: '15px' }}>
                                Our dedicated customer support team is available to clarify any doubts regarding bookings, insurance policies, or custom trip arrangements.
                            </p>
                            <div className="d-flex flex-wrap align-items-center justify-content-center" style={{ gap: '15px' }}>
                                <a href="mailto:crewtechsolution@gmail.com" className="btn btn-outline-primary px-4 py-2" style={{ borderRadius: '30px', fontWeight: 'bold' }}>
                                    <i className="fa-solid fa-envelope mr-2"></i> Email Support
                                </a>
                                <a href="tel:+917208388380" className="btn btn-primary px-4 py-2" style={{ borderRadius: '30px', fontWeight: 'bold' }}>
                                    <i className="fa-solid fa-phone mr-2"></i> +91 720 838 8380
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
}

export default Terms;